import { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import type { SandboxProcessSample } from '@reflex/shared';
import { cn } from '../../lib/utils';
import { formatBytes, formatCores } from '../../lib/format';
import { ProgressBar } from '../shared/ProgressBar';

export type ProcessSortBy = 'cpu' | 'memory';

interface ProcessUsageListProps {
  /** Processes to display. Sorted internally by `sortBy`, so callers can pass an unsorted snapshot. */
  processes: SandboxProcessSample[];
  /** Which metric drives the row ordering and the progress bar. */
  sortBy: ProcessSortBy;
  /**
   * The box's total memory (bytes). When provided, the memory bar shows each
   * process's share of the box ("%MEM", like top): the byte amount is the bold
   * value and that share-of-box percent sits at the bar's right edge. Without it,
   * the memory bar falls back to a relative scale against the largest process.
   */
  memTotalBytes?: number;
  /**
   * The box's vCPU count. When provided, the CPU bar mirrors memory: the bold
   * value becomes absolute cores used and the percent-of-cores sits at the bar's
   * right edge. Without it, the CPU bar shows the bare percent as the bold value.
   */
  cpuCores?: number;
  /** Hard cap on the total number of rows after sorting. Omit to keep all. */
  limit?: number;
  /**
   * When set and more processes exist than this, collapse to this many rows
   * behind an inline "Show N more" toggle. Expanding reveals the remaining
   * processes in the same list (never a second copy), with bars scaled against
   * the same max so collapsed and expanded rows stay visually consistent.
   */
  collapsedLimit?: number;
  /** Message shown when there are no processes to display. */
  emptyHint?: string;
  /** Optional caption rendered under the list (e.g. "Updated 3s ago"). */
  updatedLabel?: string;
  className?: string;
  'data-testid'?: string;
}

/**
 * Compact `top`-style process breakdown shared by the live sandbox monitor
 * panel and the chat performance alert. Each row shows the process name, pid,
 * the sort metric as a slim bar with its value, and the other metric as muted
 * text. The CPU bar is scaled 0-100 (percent of the box's cores); the memory
 * bar is scaled relative to the largest process in the set, since RSS has no
 * fixed ceiling here. Pass `collapsedLimit` to fold a long list behind an
 * inline disclosure instead of rendering a duplicate "show more" copy.
 */
export function ProcessUsageList({
  processes,
  sortBy,
  memTotalBytes,
  cpuCores,
  limit,
  collapsedLimit,
  emptyHint = 'No process data yet.',
  updatedLabel,
  className,
  'data-testid': testId,
}: ProcessUsageListProps) {
  const [expanded, setExpanded] = useState(false);

  const sorted = [...processes].sort((a, b) =>
    sortBy === 'cpu' ? b.cpuPct - a.cpuPct : b.memBytes - a.memBytes,
  );
  const capped = typeof limit === 'number' ? sorted.slice(0, limit) : sorted;
  // Scale every bar against the whole (capped) set so a row's bar means the
  // same thing whether the list is collapsed or expanded.
  const maxMem = Math.max(1, ...capped.map((p) => p.memBytes));

  const collapsible = typeof collapsedLimit === 'number' && capped.length > collapsedLimit;
  const visible = collapsible && !expanded ? capped.slice(0, collapsedLimit) : capped;
  const hiddenCount = collapsible ? capped.length - collapsedLimit : 0;

  if (capped.length === 0) {
    return (
      <p data-testid={testId} className={cn('px-2 text-xs text-muted-foreground', className)}>
        {emptyHint}
      </p>
    );
  }

  return (
    <div data-testid={testId} className={cn('space-y-px', className)}>
      {visible.map((process) => (
        <ProcessRow
          key={process.pid}
          process={process}
          sortBy={sortBy}
          maxMem={maxMem}
          memTotalBytes={memTotalBytes}
          cpuCores={cpuCores}
        />
      ))}
      {collapsible ? (
        <button
          type="button"
          data-testid="process-usage-toggle"
          aria-expanded={expanded}
          onClick={() => setExpanded((prev) => !prev)}
          className="mt-1 flex w-full items-center justify-center gap-1 rounded-md py-1.5 text-[11px] font-medium text-muted-foreground transition-colors hover:bg-muted/40 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {expanded ? 'Show less' : `Show ${hiddenCount} more`}
          <ChevronDown
            className={cn('h-3.5 w-3.5 transition-transform', expanded && 'rotate-180')}
            aria-hidden
          />
        </button>
      ) : null}
      {updatedLabel ? (
        <div
          className="flex items-center gap-1.5 px-2 pt-1 text-[11px] text-muted-foreground/70"
          data-testid="process-usage-updated"
        >
          <span
            className="h-1.5 w-1.5 shrink-0 animate-pulse rounded-full bg-status-success/80"
            aria-hidden
          />
          <span>{updatedLabel}</span>
        </div>
      ) : null}
    </div>
  );
}

interface ProcessRowProps {
  process: SandboxProcessSample;
  sortBy: ProcessSortBy;
  maxMem: number;
  memTotalBytes?: number;
  cpuCores?: number;
}

/**
 * One row, the same shape for either metric. The sorted metric is the
 * "primary": an absolute amount (bytes for memory, cores for CPU) as the bold
 * value, with its share-of-box percent at the bar's right edge. The other
 * metric is a dimmed aside. CPU's absolute amount needs `cpuCores` and memory's
 * percent needs `memTotalBytes`; without the total, that metric falls back to a
 * bare percent (CPU) or a relative bar (memory).
 */
function ProcessRow({ process, sortBy, maxMem, memTotalBytes, cpuCores }: ProcessRowProps) {
  const isCpu = sortBy === 'cpu';
  const cpuPct = Math.min(100, Math.max(0, process.cpuPct));

  // Each metric resolves to the same trio: a bold absolute `amount`, a `pct`
  // for the bar (and its trailing label when meaningful), and a compact
  // `secondary` for the other metric.
  const coresUsed = cpuCores && cpuCores > 0 ? (cpuPct / 100) * cpuCores : undefined;
  const memPctOfTotal =
    memTotalBytes && memTotalBytes > 0
      ? Math.min(100, Math.max(0, (process.memBytes / memTotalBytes) * 100))
      : undefined;

  const primary = isCpu
    ? {
        amount: coresUsed !== undefined ? formatCores(coresUsed) : `${Math.round(cpuPct)}%`,
        pct: cpuPct,
        // The bar percent is redundant with a bare-percent amount, so only show
        // the trailing label once the amount is an absolute (cores) value.
        trailing: coresUsed !== undefined ? `${Math.round(cpuPct)}%` : null,
      }
    : {
        amount: formatBytes(process.memBytes),
        pct: memPctOfTotal ?? (process.memBytes / maxMem) * 100,
        trailing: memPctOfTotal !== undefined ? `${Math.round(memPctOfTotal)}%` : null,
      };
  const secondary = isCpu
    ? { value: formatBytes(process.memBytes), unit: null }
    : { value: `${Math.round(cpuPct)}%`, unit: 'CPU' };

  return (
    <div
      data-testid="process-usage-row"
      className="group rounded-md px-2 py-1.5 transition-colors hover:bg-muted/40"
    >
      <div className="flex items-baseline justify-between gap-3">
        <div className="flex min-w-0 items-baseline gap-1.5">
          <span
            className="truncate text-xs font-medium text-foreground"
            title={process.command ?? process.name}
          >
            {process.name}
          </span>
          <span className="shrink-0 text-[10px] tabular-nums text-muted-foreground/40">
            pid {process.pid}
          </span>
        </div>
        <div className="flex shrink-0 items-baseline gap-2.5 tabular-nums">
          <span className="text-xs font-semibold text-foreground">{primary.amount}</span>
          {/* The non-sorted metric: context only, dimmed. */}
          <span className="flex items-baseline gap-1 text-muted-foreground/40">
            <span className="text-[10px]">{secondary.value}</span>
            {secondary.unit ? (
              <span className="text-[9px] font-medium uppercase tracking-wide">
                {secondary.unit}
              </span>
            ) : null}
          </span>
        </div>
      </div>
      <div className="mt-1.5 flex items-center gap-2">
        <ProgressBar
          value={primary.pct}
          label={`${process.name} ${isCpu ? 'CPU' : 'memory'} usage`}
          className="h-1 flex-1 bg-muted/50"
          indicatorClassName={cn(
            'rounded-full transition-[width] duration-300',
            isCpu ? 'bg-status-info' : 'bg-status-warning',
          )}
        />
        {primary.trailing ? (
          <span className="w-8 shrink-0 text-right text-[10px] tabular-nums text-muted-foreground/70">
            {primary.trailing}
          </span>
        ) : null}
      </div>
    </div>
  );
}
